import React, { useLayoutEffect, useEffect, useState, useCallback } from "react";
import "../../App.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useChat } from "../../hooks/useChat";
import { usePimathTools } from "../../hooks/usePimathTools";
import { getUserInfo } from "../../services/AuthService";
import { setMessageFeedback } from "../../services/ChatHistoryService";
import { createMistakeNote } from "../../services/MistakeNoteService";
import SidebarNavbar from "../layout/SidebarNavbar";
import TopBar from "../chat/TopBar";
import MessageList from "../chat/MessageList";
import ChatInput from "../chat/ChatInput";
import PimathToolbar from "../chat/PimathToolbar";
import ThinkingModeSelector from "../chat/ThinkingModeSelector";
import QuizPanel from "../chat/QuizPanel";
import MistakeNotebook from "../chat/MistakeNotebook";
import LearningPathModal from "../chat/LearningPathModal";
import SimilarProblemsModal from "../chat/SimilarProblemsModal";

function Dashboard() {
  const navigate = useNavigate();

  const {
    messages,
    pendingFiles,
    removePendingFile,
    handlePickedFiles,
    input,
    setInput,
    isLoading,
    selectedModel,
    setSelectedModel,
    error,
    sidebarOpen,
    setSidebarOpen,
    conversations,
    activeConversation,
    currentProjectId,
    setCurrentProjectId,
    setCurrentProjectPrompt,
    messagesScrollRef,
    textareaRef,
    handleSend,
    handleKeyDown,
    stopGenerating,
    newChat,
    switchConversation,
    deleteConversation,
    clearHistory,
    formatTime,
    editingMessageId,
    startEditMessage,
    cancelEditMessage,
    saveEditAndResend,
    regenerateFromAssistant,
    branchFromMessage,
    togglePinned,
    refreshConversationList,
    applyMessageFeedback,
    externalSystemPrompt,
    setExternalSystemPrompt,
    thinkingMode,
    setThinkingMode,
  } = useChat();

  // PIMATH Tools
  const {
    learningMode,
    setLearningMode,
    examTarget,
    setExamTarget,
    availableTargets,
    modeGroup,
    quizState,
    startQuiz,
    endQuiz,
    systemPrompt,
    placeholder,
  } = usePimathTools();

  // Sync system prompt to useChat
  useEffect(() => {
    setExternalSystemPrompt(systemPrompt);
  }, [systemPrompt, setExternalSystemPrompt]);

  // PIMATH modals state
  const [mistakeNotebookOpen, setMistakeNotebookOpen] = useState(false);
  const [learningPathOpen, setLearningPathOpen] = useState(false);
  const [similarModalOpen, setSimilarModalOpen] = useState(false);
  const [similarSourceContent, setSimilarSourceContent] = useState("");

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 769,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const userInfo = getUserInfo() || {};
  const userDisplayName =
    userInfo.username ||
    userInfo.fullName ||
    userInfo.name ||
    userInfo.userName ||
    "Người dùng";
  const userEmail = userInfo.email || userInfo.mail || "";

  const handleLogout = async () => {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== "/login" && currentPath !== "/chatbot-ai") {
      sessionStorage.setItem("redirectAfterLogin", currentPath);
    }
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleSelectSuggestion = (prompt) => {
    setInput(prompt);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  // ── PIMATH Action Handlers ──────────────────────────────────────────────

  // Save mistake from assistant message
  const handleSaveMistake = useCallback(async (messageContent, conversationId) => {
    try {
      await createMistakeNote({
        conversationId: conversationId || activeConversation,
        topic: "Khác",
        errorType: "Khác",
        wrongStep: "",
        explanation: messageContent?.slice(0, 500) || "",
        correction: "",
        reminder: "",
      });
      toast.success("Đã lưu vào sổ tay lỗi sai!");
    } catch (e) {
      toast.error("Lỗi lưu lỗi sai");
    }
  }, [activeConversation]);

  // Generate similar problems
  const handleSimilarProblems = useCallback((messageContent) => {
    setSimilarSourceContent(messageContent || "");
    setSimilarModalOpen(true);
  }, []);

  const handleSimilarConfirm = useCallback(({ count, difficulty, solutionMode }) => {
    const difficultyLabel = { easy: "dễ hơn", equivalent: "tương đương", hard: "khó hơn" }[difficulty] || "tương đương";
    const solutionLabel = { full: "lời giải chi tiết", answer_only: "chỉ đáp án", none: "không cần lời giải" }[solutionMode] || "lời giải chi tiết";

    const prompt = `Dựa trên bài toán và lời giải vừa rồi, hãy tạo ${count} bài tương tự mức độ ${difficultyLabel}. Với mỗi bài, ghi đề bài, đáp án và ${solutionLabel}. Đảm bảo không trùng số liệu quá đơn giản.\n\nBài gốc:\n${similarSourceContent.slice(0, 1000)}`;
    setInput(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [similarSourceContent, setInput, textareaRef]);

  // Formula card action
  const handleFormulaCard = useCallback((messageContent) => {
    const prompt = `Từ nội dung bài toán sau, hãy lập bảng công thức liên quan, điều kiện áp dụng, lỗi thường gặp, ví dụ mini.\n\nFormat:\n| Công thức | Điều kiện | Dùng khi nào | Lỗi thường gặp |\n|---|---|---|---|\n\nNội dung:\n${(messageContent || "").slice(0, 1000)}`;
    setInput(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [setInput, textareaRef]);

  // End quiz
  const handleEndQuiz = useCallback(() => {
    const prompt = `Kết thúc quiz. Hãy tóm tắt kết quả: tôi đã trả lời ${quizState.correct}/${quizState.total} câu đúng. Hãy phân tích điểm mạnh, điểm yếu cần ôn, và gợi ý bài tập tiếp theo.`;
    setInput(prompt);
    endQuiz();
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [quizState, endQuiz, setInput, textareaRef]);

  // Practice from mistake notebook
  const handlePracticeFromMistake = useCallback((prompt) => {
    setInput(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [setInput, textareaRef]);

  // Start day from learning path
  const handleStartDay = useCallback((prompt) => {
    setInput(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [setInput, textareaRef]);

  // Feedback up/down cho assistant message
  const handleFeedback = useCallback(
    async (uiMessageId, dbId, next) => {
      if (!activeConversation || !dbId) return;

      // Optimistic: cập nhật ngay trên UI (highlight nút like/dislike).
      applyMessageFeedback?.(dbId, next);

      try {
        await setMessageFeedback(activeConversation, dbId, next);
      } catch (e) {
        console.error("feedback:", e);
        toast.error("Không lưu được phản hồi");
        // Reload để đồng bộ lại trạng thái thật từ server.
        if (switchConversation) switchConversation(activeConversation);
      }
    },
    [activeConversation, applyMessageFeedback, switchConversation],
  );

  const showBackdrop = isMobile && sidebarOpen;

  return (
    <div
      className="w-full bg-white flex overflow-hidden"
      style={{ height: "100dvh", maxHeight: "100dvh" }}
    >
      <SidebarNavbar
        sidebarOpen={sidebarOpen}
        conversations={conversations}
        activeConversation={activeConversation}
        onNewChat={newChat}
        onSwitchConversation={switchConversation}
        onDeleteConversation={deleteConversation}
        onTogglePinned={togglePinned}
        currentProjectId={currentProjectId}
        onChangeProject={setCurrentProjectId}
        onProjectPromptChange={setCurrentProjectPrompt}
        refreshConversationList={refreshConversationList}
        onRequestClose={isMobile ? () => setSidebarOpen(false) : undefined}
      />

      {showBackdrop && (
        <div
          className="pim-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="flex-1 min-w-0 min-h-0 max-h-full overflow-hidden flex flex-col bg-white">
        <TopBar
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          onClearHistory={clearHistory}
          userDisplayName={userDisplayName}
          userEmail={userEmail}
          onLogout={handleLogout}
          activeConversationId={activeConversation}
        />

        <MessageList
          messages={messages}
          isLoading={isLoading}
          error={error}
          messagesScrollRef={messagesScrollRef}
          formatTime={formatTime}
          onSelectSuggestion={handleSelectSuggestion}
          editingMessageId={editingMessageId}
          onStartEdit={startEditMessage}
          onCancelEdit={cancelEditMessage}
          onSaveEdit={saveEditAndResend}
          onRegenerate={regenerateFromAssistant}
          onBranch={branchFromMessage}
          onFeedback={handleFeedback}
          onSaveMistake={handleSaveMistake}
          onSimilarProblems={handleSimilarProblems}
          onFormulaCard={handleFormulaCard}
          learningMode={learningMode}
          modeGroup={modeGroup}
        />

        {/* PIMATH Toolbar + Quiz Panel above input */}
        <div className="pim-chat-container pb-0">
          <div className="flex flex-col gap-2 mb-2">
            {quizState.active && (
              <QuizPanel quizState={quizState} onEndQuiz={handleEndQuiz} />
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <PimathToolbar
                learningMode={learningMode}
                onLearningModeChange={setLearningMode}
                examTarget={examTarget}
                onExamTargetChange={setExamTarget}
                targets={availableTargets}
                modeGroup={modeGroup}
              />
              <ThinkingModeSelector
                thinkingMode={thinkingMode}
                onChange={setThinkingMode}
              />
            </div>
          </div>
        </div>

        <ChatInput
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          onSend={handleSend}
          onStopGenerating={stopGenerating}
          onKeyDown={handleKeyDown}
          textareaRef={textareaRef}
          pendingFiles={pendingFiles}
          onPickFiles={handlePickedFiles}
          onRemovePendingFile={removePendingFile}
          placeholder={placeholder}
          onOpenMistakeNotebook={() => setMistakeNotebookOpen(true)}
          onOpenLearningPath={() => setLearningPathOpen(true)}
          onStartQuiz={startQuiz}
        />
      </main>

      {/* PIMATH Modals */}
      <MistakeNotebook
        open={mistakeNotebookOpen}
        onClose={() => setMistakeNotebookOpen(false)}
        onPractice={handlePracticeFromMistake}
      />
      <LearningPathModal
        open={learningPathOpen}
        onClose={() => setLearningPathOpen(false)}
        onStartDay={handleStartDay}
        examTarget={examTarget}
      />
      <SimilarProblemsModal
        open={similarModalOpen}
        onClose={() => setSimilarModalOpen(false)}
        onConfirm={handleSimilarConfirm}
      />
    </div>
  );
}

export default Dashboard;
